import { FormEvent, useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
    UpsertScheduleQualificationModalInterface,
    setError,
    setFormValues,
    setIsScheduleQualificationModalOpen,
} from "../UpsertScheduleQualificationModalSlice";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import { useGetEmployeesQuery } from "../../../common/api/employee";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import { useGetEducatorsQuery } from "../../../common/api/educator";
import {
    useEditScheduledQualificationMutation,
    useScheduleQualificationMutation,
} from "../../../common/api/scheduledQualification";
import { QualificationTypeEnum } from "../../../common/enums/QualificationTypeEnum";
import { getEmployeeOptions } from "../../../common/utils/selectOptions/getEmployeeOptions";
import { getQualificationOptions } from "../../../common/utils/selectOptions/getQualificationOptions";
import { getEducatorOptions } from "../../../common/utils/selectOptions/getEducatorOptions";
import { getEndDateMin, getStartDateMax } from "../utils/getDateBounds";
import { Option } from "../../../common/types/Types";
import { buildPrefillFormValues } from "../utils/buildPrefillFormValues";
import { deriveTitleAndSubmitLabel } from "../utils/deriveTitleAndSubmitLabel";
import { deriveIsSubmitDisabled } from "../utils/deriveIsSubmitDisabled";
import { handleEditScheduledQualification } from "../utils/handleEditScheduledQualification";
import { handleScheduleQualification } from "../utils/handleScheduleQualification";

export type UseScheduleQualificationModalResult = {
    error?: string;
    isLoading: boolean;
    isSubmitting: boolean;
    showEmployeeSelect: boolean;
    title: string;
    submitLabel: string;
    // options
    employeeOptions: Option[];
    qualificationOptions: Option[];
    educatorOptions: Option[];
    isEducatorLoading: boolean;
    // selected values
    formValues: UpsertScheduleQualificationModalInterface["formValues"];
    // bounds
    startDateMax?: string;
    endDateMin?: string;
    // handlers
    onClose: () => void;
    onEmployeeChange: (item: Option) => void;
    onQualificationChange: (item: Option) => void;
    onEducatorChange: (item: Option) => void;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    // submit
    onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
    isSubmitDisabled: boolean;
};

export const useUpsertScheduleQualificationModal =
    (): UseScheduleQualificationModalResult => {
        const dispatch = useAppDispatch();

        const isEstablishmentManager = useIsEstablishmentManager();
        const employeeId = useEmployeeId();
        const establishmentId = useEstablishmentId();

        const { error, formValues, employeeScheduledQualification } =
            useAppSelector((state) => state.upsertScheduleQualificationModal);

        // Queries
        const { data: employeeData, isLoading: employeeDataIsLoading } =
            useGetEmployeesQuery(
                {
                    items_per_page: 9999,
                    filter: {
                        establishment_id: establishmentId,
                    },
                },
                { skip: !isEstablishmentManager },
            );

        const {
            data: qualificationData,
            isLoading: qualificationDataIsLoading,
        } = useGetQualificationsQuery({
            items_per_page: 9999,
            filter: { type: QualificationTypeEnum.PERSON },
        });

        const { data: educatorData, isFetching: educatorDataIsFetching } =
            useGetEducatorsQuery({
                items_per_page: 9999,
                filter: { qualification_id: formValues?.qualification?.value },
            });

        const [
            scheduleQualification,
            { isLoading: scheduleQualificationIsLoading },
        ] = useScheduleQualificationMutation();
        const [
            editScheduleQualification,
            { isLoading: editScheduleQualificationIsLoading },
        ] = useEditScheduledQualificationMutation();

        useEffect(() => {
            const values = buildPrefillFormValues({
                employeeScheduledQualification,
                isEstablishmentManager: Boolean(isEstablishmentManager),
                employeeId,
            });
            if (values) dispatch(setFormValues(values));
        }, [
            dispatch,
            employeeScheduledQualification,
            isEstablishmentManager,
            employeeId,
        ]);

        // Options
        const employeeOptions = useMemo(
            () => getEmployeeOptions(employeeData?.data),
            [employeeData?.data],
        );

        const qualificationOptions = useMemo(
            () => getQualificationOptions(qualificationData?.data ?? []),
            [qualificationData?.data],
        );

        const educatorOptions = useMemo(
            () => getEducatorOptions(educatorData?.data),
            [educatorData?.data],
        );

        const isLoading =
            qualificationDataIsLoading ||
            !qualificationData ||
            !qualificationData.data ||
            (isEstablishmentManager &&
                (employeeDataIsLoading || !employeeData || !employeeData.data));

        // Handlers
        const onClose = (): void => {
            dispatch(setIsScheduleQualificationModalOpen(false));
            dispatch(setError(null));
        };

        const onEmployeeChange = (item: Option): void => {
            dispatch(
                setFormValues({
                    ...formValues,
                    employee: { value: item.value, label: item.label },
                }),
            );
        };

        const onQualificationChange = (item: Option): void => {
            dispatch(
                setFormValues({
                    ...formValues,
                    qualification: { value: item.value, label: item.label },
                }),
            );
        };

        const onEducatorChange = (item: Option): void => {
            dispatch(
                setFormValues({
                    ...formValues,
                    educator: { value: item.value, label: item.label },
                }),
            );
        };

        const onStartDateChange = (value: string): void => {
            // Allow setting startDate after current endDate. If that happens,
            // automatically align endDate to the same day.
            const next: NonNullable<
                UpsertScheduleQualificationModalInterface["formValues"]
            > = {
                ...(formValues ??
                    ({} as NonNullable<
                        UpsertScheduleQualificationModalInterface["formValues"]
                    >)),
                startDate: value,
            };

            if (formValues?.endDate && value > formValues.endDate) {
                next.endDate = value;
            }

            dispatch(setFormValues(next));
        };

        const onEndDateChange = (value: string): void => {
            dispatch(
                setFormValues({
                    ...formValues,
                    endDate: value,
                }),
            );
        };

        const startDateMax = getStartDateMax(formValues?.endDate);
        const endDateMin = getEndDateMin(formValues?.startDate);

        const isSubmitDisabled = deriveIsSubmitDisabled({
            formValues,
            isEstablishmentManager: Boolean(isEstablishmentManager),
        });

        const isEditMode = Boolean(
            formValues?.employeeScheduledQualificationId ||
            employeeScheduledQualification,
        );

        const onSubmit = useCallback(
            async (event: FormEvent<HTMLFormElement>): Promise<void> => {
                event.preventDefault();
                if (!formValues) return;

                if (isEditMode && formValues.employeeScheduledQualificationId) {
                    await handleEditScheduledQualification({
                        formValues,
                        editScheduleQualification,
                        dispatch,
                    });
                    return;
                }

                await handleScheduleQualification({
                    formValues,
                    scheduleQualification,
                    dispatch,
                    establishmentId,
                });
            },
            [
                dispatch,
                editScheduleQualification,
                isEditMode,
                scheduleQualification,
                formValues,
                establishmentId,
            ],
        );

        return {
            error,
            isLoading: Boolean(isLoading),
            isSubmitting:
                Boolean(scheduleQualificationIsLoading) ||
                Boolean(editScheduleQualificationIsLoading),
            showEmployeeSelect: Boolean(isEstablishmentManager),
            ...deriveTitleAndSubmitLabel(isEditMode),
            employeeOptions,
            qualificationOptions,
            educatorOptions,
            isEducatorLoading: Boolean(educatorDataIsFetching),
            formValues,
            startDateMax,
            endDateMin,
            onClose,
            onEmployeeChange,
            onQualificationChange,
            onEducatorChange,
            onStartDateChange,
            onEndDateChange,
            onSubmit,
            isSubmitDisabled,
        };
    };
